import React, { useState, useEffect } from 'react';
import { motion } from "framer-motion";
import { useLocation } from 'react-router-dom';
import { FaPlaneDeparture, FaPlaneArrival } from "react-icons/fa";
import { useNavigate } from 'react-router-dom';
import PlaneSpinner from './FlightLoadingScreen';
import SimpleForm from './CheckoutForm';
import axios from 'axios'; // For making HTTP requests to your Node.js backend

const CheckoutPage = () => {
  const location = useLocation();
  const departureFlight = location?.state?.departureFlight || null;
  const arrivalFlight = location?.state?.arrivalFlight || null;
  const children = location?.state?.children || null;
  const adults = location?.state?.adults || null;
  const infants = location?.state?.infants || null;
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [contactInfo, setContactInfo] = useState({ email: '', phone: '' });
  const [travellerInfo, setTravellerInfo] = useState({
    title: '',
    firstName: '',
    lastName: '',
    dob: '',
    gender: '',
  });
  const [billingInfo, setBillingInfo] = useState({
    address1: '',
    address2: '',
    city: '',
    country: '',
    state: '',
    zip: '',
  });

  const countryStateMap = {
    India: [
      "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
      "Delhi", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand",
      "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
      "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan",
      "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttarakhand", "Uttar Pradesh",
      "West Bengal"
    ],
    USA: [
      "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
      "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois",
      "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland",
      "Massachusetts", "Michigan", "Minnesota", "Mississippi", "Missouri", "Montana",
      "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico", "New York",
      "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania",
      "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah",
      "Vermont", "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming"
    ],
    Canada: [
      "Alberta", "British Columbia", "Manitoba", "New Brunswick", "Newfoundland and Labrador",
      "Nova Scotia", "Ontario", "Prince Edward Island", "Quebec", "Saskatchewan",
      "Northwest Territories", "Nunavut", "Yukon"
    ]
  };

  // PaymentInfo state will now primarily manage cardHolder name,
  // as card number, expiry, and CVV are handled by Collect.js iframes.
  const [paymentInfo, setPaymentInfo] = useState({
    cardHolder: '',
  });

  // States for NMI payment processing feedback
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  // This is your NMI Public API Key for client-side tokenization.
  const NMI_PUBLIC_API_KEY = "S3xqzC-A29h4E-vhfc7b-2uP5Xz"; // <<< Replace with your actual Public API Key

  // Base URL for your Node.js backend
  const API_BASE_URL = 'https://BookTrip4Me-backend.onrender.com';

  // Calculate total price dynamically
  const departurePrice = departureFlight ? parseFloat(departureFlight.price) : 0;
  const arrivalPrice = arrivalFlight ? parseFloat(arrivalFlight.price) : 0;
  const basePrice = arrivalFlight ? arrivalPrice : departurePrice;
  const taxesAndFees = 15;
  const discount = 5;
  const totalPrice = (basePrice + taxesAndFees - discount).toFixed(2); // fixed-point

  let session_id = localStorage.getItem("session_id");
  if (!session_id) {
    session_id = `SESSION-${Date.now()}`;
    localStorage.setItem("session_id", session_id);
  }

  const submitPartialData = async () => {
    try {
      const firstDep = departureFlight?.flights?.[0];
      const lastDep = departureFlight?.flights?.[departureFlight?.flights?.length - 1];
      const depDurationMin = (new Date(lastDep?.arrival_airport?.time) - new Date(firstDep?.departure_airport?.time)) / 60000;
      const depHours = Math.floor(depDurationMin / 60);
      const depMinutes = depDurationMin % 60;

      const firstArr = arrivalFlight?.flights?.[0];
      const lastArr = arrivalFlight?.flights?.[arrivalFlight?.flights?.length - 1];
      const arrDurationMin = (new Date(lastArr?.arrival_airport?.time) - new Date(firstArr?.departure_airport?.time)) / 60000;
      const arrHours = Math.floor(arrDurationMin / 60);
      const arrMinutes = arrDurationMin % 60;

      const search_id = localStorage.getItem('search_id');

      await axios.post(`https://booktrip4me.com/api/submit-sheet.php`, {
        search_id,
        first_name: travellerInfo.firstName,
        last_name: travellerInfo.lastName,
        email: contactInfo.email,
        phone: contactInfo.phone,
        address1: billingInfo.address1,
        city: billingInfo.city,
        state: billingInfo.state,
        zip: billingInfo.zip,
        country: billingInfo.country,
        amount: totalPrice,
        order_id: `ORDER-${Date.now()}`,

        // Departure summary
        departure_time: new Date(firstDep?.departure_airport?.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        departure_airport: `${firstDep?.departure_airport?.name} (${firstDep?.departure_airport?.id})`,
        departure_duration: `${depHours}h ${depMinutes}m`,
        departure_stops: (departureFlight?.flights?.length || 1) - 1,
        departure_overnight: departureFlight?.overnight ? 'Yes' : 'No',
        arrival_time: new Date(lastDep?.arrival_airport?.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        arrival_airport: `${lastDep?.arrival_airport?.name} (${lastDep?.arrival_airport?.id})`,

        // Arrival summary (if round trip)
        return_departure_time: new Date(firstArr?.departure_airport?.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        return_departure_airport: `${firstArr?.departure_airport?.name} (${firstArr?.departure_airport?.id})`,
        return_duration: `${arrHours}h ${arrMinutes}m`,
        return_stops: (arrivalFlight?.flights?.length || 1) - 1,
        return_overnight: arrivalFlight?.overnight ? 'Yes' : 'No',
        return_arrival_time: new Date(lastArr?.arrival_airport?.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        return_arrival_airport: `${lastArr?.arrival_airport?.name} (${lastArr?.arrival_airport?.id})`
      });

    } catch (err) {
      console.error('Error submitting partial data:', err);
    }
  };

  const isStepCompleted = (step) => completedSteps.includes(step);

  const completeStep = (step) => {
    setCompletedSteps((prev) => [...new Set([...prev, step])]);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (contactInfo.email && contactInfo.phone) {
      completeStep(1);
      setCurrentStep(2);
      await submitPartialData();
    }
  };

  const handleTravellerSubmit = async (e) => {
    e.preventDefault();
    const { title, firstName, lastName, dob, gender } = travellerInfo;
    if (title && firstName && lastName && dob && gender) {
      completeStep(2);
      setCurrentStep(3);
      await submitPartialData();
    }
  };

  const handleBillingSubmit = async (e) => {
    e.preventDefault();
    const { address1, city, country, state, zip } = billingInfo;
    if (address1 && city && country && state && zip) {
      completeStep(3);
      setCurrentStep(4);
      await submitPartialData();
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    const { address1, city, country, state, zip } = billingInfo;
    if (address1 && city && country && state && zip) {
      completeStep(3);
      setCurrentStep(4);
      await submitPartialData();
    }
    localStorage.setItem('amount', totalPrice);
    window.location.href = "/Payment.html";
  };

  const renderFlightCard = (result, type) => {
    const allFlights = result.flights;
    const first = allFlights[0];
    const last = allFlights[allFlights.length - 1];
    const durationMin = (new Date(last.arrival_airport.time) - new Date(first.departure_airport.time)) / 60000;
    const hours = Math.floor(durationMin / 60);
    const minutes = durationMin % 60;
    const stopsCount = allFlights.length - 1;

    return (
      <div className="bg-white/95 border border-teal-100 rounded-2xl shadow-sm hover:shadow-md transition-shadow mb-5 p-4 md:p-6">
        {/* Header pill */}
        <div className="flex items-center justify-between mb-3">
          <span className="inline-flex items-center gap-2 rounded-full bg-teal-50 text-teal-700 text-xs font-semibold px-3 py-1">
            {type === "Departure Flight" ? (
              <FaPlaneDeparture className="text-teal-500" />
            ) : (
              <FaPlaneArrival className="text-teal-500" />
            )}
            {arrivalFlight ? type : "Selected Flight"}
          </span>
          <span className="text-[11px] uppercase tracking-wide text-gray-400">
            BookTrip4Me · Best value
          </span>
        </div>

        {/* Compact layout */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          {/* Departure Info */}
          <div className="text-center md:text-left flex-1 min-w-[120px]">
            <div className="text-xl font-bold text-gray-900">
              {new Date(first.departure_airport.time).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {first.departure_airport.name} ({first.departure_airport.id})
            </div>
          </div>

          {/* Duration + Stops */}
          <div className="text-center text-sm text-gray-600 min-w-[110px]">
            <div className="font-semibold">
              {hours}h {minutes}m
            </div>
            <div className="text-xs mt-1">
              {stopsCount === 0 ? 'Non-stop' : `${stopsCount} stop${stopsCount > 1 ? 's' : ''}`}
            </div>
            {result.overnight && (
              <div className="mt-1 inline-flex px-2 py-0.5 rounded-full bg-orange-50 text-[10px] text-orange-600 font-semibold">
                Overnight
              </div>
            )}
          </div>

          {/* Arrival Info */}
          <div className="text-center md:text-right flex-1 min-w-[120px]">
            <div className="text-xl font-bold text-gray-900">
              {new Date(last.arrival_airport.time).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {last.arrival_airport.name} ({last.arrival_airport.id})
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full min-h-screen bg-gradient-to-b from-[#F3FFF8] via-[#F9FEFF] to-[#F3FFF8] py-8 px-4">
      <link
        href="https://unpkg.com/tailwindcss@2.2.19/dist/tailwind.min.css"
        rel="stylesheet"
      />

      <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-6">
        {/* LEFT: Flight summary + multi-step form */}
        <motion.div
          className="lg:w-2/3 w-full space-y-5"
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {/* Small breadcrumb / page label */}
          <div className="mb-2">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-teal-50 text-teal-700 text-xs font-semibold">
              Secure Checkout · BookTrip4Me
            </span>
          </div>

          {/* Flight cards */}
          {departureFlight && renderFlightCard(departureFlight, "Departure Flight")}
          {arrivalFlight && renderFlightCard(arrivalFlight, "Arrival Flight")}

          {/* Form card wrapper to match mint / soft theme */}
          <div className="bg-white/95 border border-gray-200 rounded-3xl shadow-md p-4 md:p-6">
            <SimpleForm
              departureFlight={departureFlight}
              arrivalFlight={arrivalFlight}
              travellerInfo={travellerInfo}
              contactInfo={contactInfo}
              billingInfo={billingInfo}
            />
          </div>
        </motion.div>

        {/* RIGHT: Fare details – sticky summary card */}
        <motion.div
          className="lg:w-1/3 w-full sm:order-2"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.1 }}
        >
          <div className="sticky top-24">
            <div className="bg-white/95 border border-teal-100 rounded-3xl shadow-lg w-full max-w-sm mx-auto p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">
                Fare Summary
              </h2>
              <p className="text-xs text-gray-500 mb-4">
                All prices shown in USD. Taxes included.
              </p>

              <hr className="border-gray-200 mb-4" />

              <div className="space-y-3 text-sm">
                <div className="flex justify-between items-center">
                  <div className="text-gray-700 space-y-1">
                    <p className="font-semibold">
                      Adult ({adults})
                    </p>
                    {children > 0 && (
                      <p className="font-semibold">
                        Children ({children})
                      </p>
                    )}
                    {infants > 0 && (
                      <p className="font-semibold">
                        Infants ({infants})
                      </p>
                    )}
                  </div>
                  <p className="text-gray-800 font-semibold">
                    ${basePrice.toFixed(2)}
                  </p>
                </div>

                <div className="flex justify-between items-center">
                  <p className="font-medium text-gray-700">Taxes &amp; Fees</p>
                  <p className="text-gray-800 font-medium">
                    ${taxesAndFees.toFixed(2)}
                  </p>
                </div>

                <div className="flex justify-between items-center">
                  <p className="font-medium text-emerald-600">Discount</p>
                  <p className="text-emerald-600 font-semibold">
                    -${discount.toFixed(2)}
                  </p>
                </div>
              </div>

              <hr className="border-gray-200 my-4" />

              <div className="flex justify-between items-center border border-orange-200 bg-orange-50 rounded-2xl px-4 py-3">
                <p className="font-bold text-gray-900 text-base">
                  Total Price
                </p>
                <p className="font-extrabold text-gray-900 text-lg">
                  ${totalPrice}
                </p>
              </div>

              <p className="mt-3 text-[11px] text-gray-500 text-center">
                Secure payment processed via encrypted gateway.  
                Your booking details will be emailed once completed.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CheckoutPage;
